import { useEffect, useRef, useState } from 'react';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as userActions } from '@store/slice/user-slice';
import axios from '@config/axios-config';
import { SERVICE, IS_REACT_NATIVE_APP } from '@config/environment';
import { VERSION_KEY } from '@constant/version';
import {
  LOCAL_STORAGE_ACCESS_TOKEN_KEY,
  LOCAL_STORAGE_REFRESH_TOKEN_KEY,
  LOCAL_STORAGE_USER_KEY,
  LOCAL_STORAGE_STUDENT_KEY,
} from '@config/auth';
import { EService } from '@datatype/type';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { MOASHELF_PATH } from '@constant/moashelf/route-path';
import { IReleaseVersionData } from '@datatype/common/api-type';
import { useCheckApplicationMetadata } from '@hook/common/useApplication';

/**
 * @description 백엔드의 release version과 localStorage의 version을 비교하여 mismatch 시 업데이트 모달 노출.
 *
 * - 마운트 시 1회 + 10분마다 자동 재체크 (setInterval)
 * - mismatch 시: 업데이트 모달 노출(닫기 불가) → 사용자가 '새로고침' 클릭하면
 *   localStorage.clear() (studentKey만 보존) → window.location.href reload
 */
export const useWebVersionCheck = () => {
  const dispatch = useTypedDispatch();
  const [needRefresh, setNeedRefresh] = useState(false);
  // 모달에 노출할 새 버전 + 변경사항
  const [releaseVersion, setReleaseVersion] = useState<string | null>(null);
  const [releaseNote, setReleaseNote] = useState<string | null>(null);
  // 모달의 '새로고침' 버튼 클릭을 기다리는 resolve
  const confirmRef = useRef<(() => void) | null>(null);
  const endpoint = `/api/v1/version?service=${SERVICE}`;

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await axios.get<IReleaseVersionData>(endpoint).then((res) => res.data);
        const releasedVersion = response.web.releaseVersion;
        const currentVersion = localStorage.getItem(VERSION_KEY) ?? null;

        // 처음 방문 시 (version 값이 없을 때) → 저장만 하고 리다이렉트 X
        if (!currentVersion) {
          localStorage.setItem(VERSION_KEY, releasedVersion);
          return;
        }

        // version이 다를 때 → 업데이트 후 리다이렉트
        if (releasedVersion && currentVersion !== releasedVersion) {
          console.error(`release version update ${currentVersion} -> ${releasedVersion}`);

          // 업데이트 모달 노출 후, 사용자가 '새로고침'을 누를 때까지 대기
          setReleaseVersion(releasedVersion);
          setReleaseNote(response.web.releaseNote ?? null);
          setNeedRefresh(true);
          // 모달을 10분 넘게 방치하면 interval이 중복으로 한 번 더 체크할 뿐이라 그대로 둠
          // clearInterval(intervalId);
          await new Promise<void>((resolve) => (confirmRef.current = resolve));

          // storage에 version 저장
          const userKey = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
          const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
          const refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
          // iyagigo: guest student key 보존
          const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY);

          localStorage.clear();

          // local storage 정리 후, 보존할 key 복구
          localStorage.setItem(VERSION_KEY, releasedVersion);

          if (studentKey) {
            localStorage.setItem(LOCAL_STORAGE_STUDENT_KEY, studentKey);
          }

          if (userKey && accessToken && refreshToken) {
            dispatch(
              userActions.authenticate({
                userKey,
                accessToken,
                refreshToken,
              }),
            );
          }

          // cache 비우기
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
          }

          // [폐기] 자동 reload 시절, splash(useTodayDataInit)가 INITIAL_TODAY_DATE_KEY를 저장할 시간 확보용 대기
          // - 모달이 떠 있는 동안 splash가 끝나므로 불필요
          // (splash 최대 노출 시간 5000ms + 여유 1000ms)
          // await new Promise((resolve) => setTimeout(resolve, 6000));
          // 각 서비스의 INITIAL_SERVICE_PATH로 이동
          switch (SERVICE) {
            case EService.IYAGIGO:
              window.location.href = IYAGIGO_PATH.INITIAL_SERVICE_PATH;
              break;
            case EService.MOASHELF:
              window.location.href = MOASHELF_PATH.BEGIN_PATH;
              break;
          }
        }
      } catch (error) {
        console.error('release version check fail:', error);
      }
    };

    checkVersion();
    const intervalId = setInterval(checkVersion, 10 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [endpoint, dispatch]);

  // 모달의 '새로고침' 버튼 → 대기 중인 checkVersion 재개
  const handleRefresh = () => confirmRef.current?.();

  return { needRefresh, releaseVersion, releaseNote, handleRefresh };
};

// ###################################
// 앱(React Native) 버전 체크
// ###################################

/**
 * semver 비교
 * @returns -1: a < b, 0: a == b, 1: a > b
 */
const compareVersion = (a: string, b: string): number => {
  const partsA = a.split('.').map(Number);
  const partsB = b.split('.').map(Number);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  return 0;
};

/**
 * @description 앱 버전과 백엔드의 플랫폼별 minVersion/releaseVersion을 비교해 업데이트 모달 노출.
 *
 * - 웹 버전 체크(useWebVersionCheck)와 분리: 비교 방식(semver), 해결 수단(스토어 이동), 강제성이 모두 다르다.
 * - 이야기고 서비스의 앱 WebView에서만 동작한다. 응답 대기 중(isChecked === false)에는 판단하지 않는다.
 * - 재시도까지 끝나도 버전을 못 받으면(version === '-') 구버전으로 보고 권장 안내만 한다.
 *   일시적인 브리지 실패와 구분할 수 없으므로 강제하지 않는다.
 * - minVersion 미만: 강제(닫기 불가) / releaseVersion 미만: 권장(닫기 가능)
 */
export const useApplicationVersionCheck = () => {
  const { platform, version, isChecked } = useCheckApplicationMetadata();
  const [needUpdate, setNeedUpdate] = useState(false);
  const [isForce, setIsForce] = useState(false);
  const [releaseNote, setReleaseNote] = useState<string | null>(null);

  useEffect(() => {
    // 스토어에 출시된 앱은 이야기고뿐이다. 다른 서비스는 안내할 스토어가 없으므로 체크하지 않는다.
    if (SERVICE !== EService.IYAGIGO) return;
    if (!IS_REACT_NATIVE_APP) return;
    // 아직 응답 대기 중이면 판단하지 않는다 (최신 앱이 잠깐 모달을 보는 것을 막는다)
    if (!isChecked) return;

    // 재시도까지 끝났는데 버전을 못 받았다면 CHECK_APPLICATION_METADATA를 모르는 구버전일 가능성이 높다.
    // 다만 일시적인 브리지 실패와 구분할 수 없으므로 강제하지 않고 권장으로만 안내한다.
    if (version === '-') {
      setIsForce(false);
      setReleaseNote(null);
      setNeedUpdate(true);
      return;
    }

    (async () => {
      try {
        const response = await axios
          .get<IReleaseVersionData>(`/api/v1/version?service=${SERVICE}`)
          .then((res) => res.data);
        const platformVersion = platform === 'ios' ? response.ios : response.android;
        if (!platformVersion) return;

        const { minVersion, releaseVersion, releaseNote: note } = platformVersion;
        console.info(
          `application version check: current(${platform}/${version}) / min(${minVersion}) / release(${releaseVersion})`,
        );

        if (compareVersion(version, minVersion) < 0) {
          setIsForce(true);
          setReleaseNote(note);
          setNeedUpdate(true);
        } else if (compareVersion(version, releaseVersion) < 0) {
          setIsForce(false);
          setReleaseNote(note);
          setNeedUpdate(true);
        }
      } catch (error) {
        console.error('application version check fail:', error);
      }
    })();
  }, [platform, version, isChecked]);

  const handleDismiss = () => setNeedUpdate(false);

  return { needUpdate, isForce, releaseNote, handleDismiss };
};
